import { useState, useEffect, useMemo } from 'react';
import {
  X, Copy, Check, KeyRound, ExternalLink, Loader2, Sparkles,
  Users, GraduationCap, HeartHandshake, Search, Info, ShieldCheck,
  Shield, Building2, MapPin, Globe, LogIn, RefreshCw, Send,
  UserCheck, AlertCircle, CheckCircle2, Phone, Mail,
} from 'lucide-react';
import { api } from '../../api/client';
import { toast } from 'sonner';
import { useImpersonate } from '../../hooks/useImpersonate';

interface UsuarioAcesso {
  id: string;
  nome: string;
  email: string;
  perfil: string;
  cargo?: string | null;
  telefone?: string | null;
  cpf?: string | null;
  ativo: boolean;
  tokenAtivacao?: string | null;
  statusConta?: string | null;
  ultimoLogin?: string | null;
}

interface ResponsavelAcesso {
  id: string;
  nome: string;
  email: string | null;
  telefone: string | null;
  cpf: string | null;
  ativo: boolean;
  usuarioId?: string | null;
  usuario?: {
    id: string;
    nome: string;
    email: string;
    perfil: string;
    ativo: boolean;
    tokenAtivacao?: string | null;
    statusConta?: string | null;
    ultimoLogin?: string | null;
  } | null;
  alunos?: { aluno: { id: string; nome: string; turma?: { nome: string } | null } }[];
}

interface EscolaAcessosResponse {
  escola: {
    id: string;
    nome: string;
    codigo?: string;
    municipio?: string;
    estado?: string;
    sigla?: string;
    rede?: string;
  };
  gestoresMunicipais: UsuarioAcesso[];
  diretores: UsuarioAcesso[];
  equipe: UsuarioAcesso[];
  professores: UsuarioAcesso[];
  responsaveis: ResponsavelAcesso[];
}

interface EscolaAcessosRapidosModalProps {
  escolaId: string;
  escolaNome: string;
  isOpen: boolean;
  onClose: () => void;
}

const CARGO_CONFIG: Record<string, { label: string; bg: string; color: string; border: string }> = {
  MUNICIPAL:   { label: 'GESTOR MUNICIPAL', bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
  DIRETOR:     { label: 'DIRETOR(A)',       bg: '#ecfdf5', color: '#047857', border: '#a7f3d0' },
  COORDENACAO: { label: 'COORDENAÇÃO',      bg: '#f0fdfa', color: '#0f766e', border: '#99f6e4' },
  SECRETARIA:  { label: 'SECRETARIA',       bg: '#f8fafc', color: '#334155', border: '#cbd5e1' },
  ESCOLA:      { label: 'EQUIPE ESCOLAR',   bg: '#f8fafc', color: '#475569', border: '#e2e8f0' },
  PROFESSOR:   { label: 'PROFESSOR(A)',     bg: '#fef3c7', color: '#b45309', border: '#fde68a' },
  RESPONSAVEL: { label: 'RESPONSÁVEL',      bg: '#f3e8ff', color: '#7e22ce', border: '#e9d5ff' },
  ADMIN:       { label: 'ADMINISTRADOR',    bg: '#fee2e2', color: '#b91c1c', border: '#fca5a5' },
};

function CopyButton({
  text,
  label,
  buttonText,
  icon,
}: {
  text: string;
  label?: string;
  buttonText?: string;
  icon?: React.ReactNode;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      toast.success(`${label ?? 'Texto'} copiado com sucesso!`);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <button
      onClick={handleCopy}
      type="button"
      style={{
        background: copied ? '#dcfce7' : '#f8fafc',
        border: copied ? '1.5px solid #86efac' : '1.5px solid #cbd5e1',
        color: copied ? '#166534' : '#334155',
        borderRadius: 6,
        padding: '5px 9px',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        fontSize: '.74rem',
        fontWeight: 600,
        transition: 'all .15s ease',
      }}
    >
      {copied ? <Check size={12} /> : (icon ?? <Copy size={12} />)}
      {copied ? 'Copiado!' : (buttonText ?? 'Copiar')}
    </button>
  );
}

export function EscolaAcessosRapidosModal({
  escolaId,
  escolaNome,
  isOpen,
  onClose,
}: EscolaAcessosRapidosModalProps) {
  const [dados, setDados] = useState<EscolaAcessosResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('TODOS');
  const [generatingLinkFor, setGeneratingLinkFor] = useState<string | null>(null);

  const { impersonate, impersonating } = useImpersonate();

  const fetchDados = () => {
    setLoading(true);
    api.get(`/admin/escolas/${escolaId}/acessos-rapidos`)
      .then((res) => setDados(res.data))
      .catch((err) => {
        toast.error('Erro ao carregar acessos da escola');
        setDados(null);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!isOpen) return;
    setSearchQuery('');
    setActiveCategory('TODOS');
    fetchDados();
  }, [isOpen, escolaId]);

  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';

  const getDirectLoginLink = (email: string) => {
    return `${baseUrl}/login?email=${encodeURIComponent(email)}`;
  };

  const handleGenerateNewLink = async (userId: string, userNome: string) => {
    setGeneratingLinkFor(userId);
    try {
      const res = await api.post(`/admin/usuarios/${userId}/resend`);
      const link = res.data?.activationLink || `${baseUrl}/login`;
      await navigator.clipboard.writeText(link);
      toast.success(`Novo link gerado e copiado para ${userNome}!`);
      fetchDados();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erro ao gerar novo link');
    } finally {
      setGeneratingLinkFor(null);
    }
  };

  // Filtros de busca
  const q = searchQuery.toLowerCase().trim();

  const filterUser = (u: UsuarioAcesso) => {
    if (!q) return true;
    const full = [u.nome, u.email, u.cargo || '', u.perfil, u.telefone || '', u.cpf || ''].join(' ').toLowerCase();
    return full.includes(q);
  };

  const gestoresFiltrados = useMemo(() => (dados?.gestoresMunicipais || []).filter(filterUser), [dados, q]);
  const diretoresFiltrados = useMemo(() => (dados?.diretores || []).filter(filterUser), [dados, q]);
  const equipeFiltrada = useMemo(() => (dados?.equipe || []).filter(filterUser), [dados, q]);
  const professoresFiltrados = useMemo(() => (dados?.professores || []).filter(filterUser), [dados, q]);

  const responsaveisFiltrados = useMemo(() => {
    return (dados?.responsaveis || []).filter((r) => {
      if (!q) return true;
      const alunosNomes = (r.alunos || []).map((a) => a.aluno?.nome || '').join(' ');
      const full = [r.nome, r.email || '', r.telefone || '', r.cpf || '', alunosNomes].join(' ').toLowerCase();
      return full.includes(q);
    });
  }, [dados, q]);

  const totalGestores = (dados?.gestoresMunicipais || []).length;
  const totalDiretores = (dados?.diretores || []).length;
  const totalEquipe = (dados?.equipe || []).length;
  const totalProfessores = (dados?.professores || []).length;
  const totalResponsaveis = (dados?.responsaveis || []).length;
  const totalGeral = totalGestores + totalDiretores + totalEquipe + totalProfessores + totalResponsaveis;

  if (!isOpen) return null;

  const renderUserCard = (
    u: UsuarioAcesso,
    tipo: 'MUNICIPAL' | 'DIRETOR' | 'EQUIPE' | 'PROFESSOR',
    extraBadge?: string
  ) => {
    const cargoKey = u.cargo?.toUpperCase() || u.perfil?.toUpperCase() || tipo;
    const conf = CARGO_CONFIG[cargoKey] || CARGO_CONFIG[tipo] || CARGO_CONFIG.ESCOLA;
    const directLink = getDirectLoginLink(u.email);
    const isThisImpersonating = impersonating === u.id;
    const isGenerating = generatingLinkFor === u.id;

    return (
      <div
        key={u.id}
        style={{
          background: '#ffffff',
          borderRadius: 10,
          border: '1.5px solid #e2e8f0',
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
          boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
        }}
      >
        {/* Lado Esquerdo: Avatar, Nome, Cargo e Email */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 260, flex: '1 1 280px' }}>
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: '50%',
              background: conf.bg,
              color: conf.color,
              border: `1.5px solid ${conf.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 800,
              fontSize: '.9rem',
              flexShrink: 0,
            }}
          >
            {u.nome?.charAt(0).toUpperCase() || 'U'}
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <strong style={{ fontSize: '.88rem', color: '#0f172a' }}>{u.nome}</strong>
              <span
                style={{
                  padding: '2px 7px',
                  borderRadius: 6,
                  fontSize: '.68rem',
                  fontWeight: 700,
                  background: conf.bg,
                  color: conf.color,
                  border: `1px solid ${conf.border}`,
                  textTransform: 'uppercase',
                }}
              >
                {extraBadge || conf.label}
              </span>
              {u.ativo ? (
                <span style={{ fontSize: '.68rem', fontWeight: 600, color: '#16a34a', display: 'flex', alignItems: 'center', gap: 3 }}>
                  <CheckCircle2 size={11} /> Ativo
                </span>
              ) : (
                <span style={{ fontSize: '.68rem', fontWeight: 600, color: '#d97706', display: 'flex', alignItems: 'center', gap: 3 }}>
                  <AlertCircle size={11} /> Pendente
                </span>
              )}
            </div>

            <div style={{ fontSize: '.76rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: 10, marginTop: 2 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Mail size={12} /> {u.email}
              </span>
              {u.telefone && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Phone size={11} /> {u.telefone}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Lado Direito: Ações Rápidas (Copiar Email, Copiar Link, Gerar Novo Link, Entrar) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <CopyButton text={u.email} label="E-mail" buttonText="Copiar E-mail" />
          <CopyButton text={directLink} label="Link Direto" buttonText="Copiar Link" />

          <button
            onClick={() => handleGenerateNewLink(u.id, u.nome)}
            disabled={isGenerating}
            type="button"
            style={{
              background: '#f8fafc',
              border: '1.5px solid #cbd5e1',
              color: '#475569',
              borderRadius: 6,
              padding: '5px 9px',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              fontSize: '.74rem',
              fontWeight: 600,
            }}
            title="Reemitir token e link de ativação"
          >
            <RefreshCw size={11} className={isGenerating ? 'animate-spin' : ''} />
            {isGenerating ? 'Gerando...' : 'Gerar Novo Link'}
          </button>

          <button
            onClick={() => impersonate(u.id)}
            disabled={isThisImpersonating || !u.ativo}
            type="button"
            style={{
              background: u.ativo ? 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' : '#e2e8f0',
              border: 'none',
              color: u.ativo ? '#ffffff' : '#94a3b8',
              borderRadius: 6,
              padding: '5px 12px',
              cursor: u.ativo ? 'pointer' : 'not-allowed',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              fontSize: '.76rem',
              fontWeight: 700,
              boxShadow: u.ativo ? '0 2px 6px rgba(79, 70, 229, 0.25)' : 'none',
            }}
            title="Abrir sessão direta em nova aba"
          >
            <LogIn size={12} />
            {isThisImpersonating ? 'Abrindo...' : 'Entrar'}
          </button>
        </div>
      </div>
    );
  };

  const renderResponsavelCard = (r: ResponsavelAcesso) => {
    const conf = CARGO_CONFIG.RESPONSAVEL;
    const userId = r.usuario?.id || r.usuarioId;
    const email = r.email || r.usuario?.email || '';
    const directLink = email ? getDirectLoginLink(email) : '';
    const isThisImpersonating = userId ? impersonating === userId : false;
    const isGenerating = userId ? generatingLinkFor === userId : false;

    return (
      <div
        key={r.id}
        style={{
          background: '#ffffff',
          borderRadius: 10,
          border: '1.5px solid #e2e8f0',
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
          boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 260, flex: '1 1 280px' }}>
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: '50%',
              background: conf.bg,
              color: conf.color,
              border: `1.5px solid ${conf.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 800,
              fontSize: '.9rem',
              flexShrink: 0,
            }}
          >
            {r.nome?.charAt(0).toUpperCase() || 'R'}
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <strong style={{ fontSize: '.88rem', color: '#0f172a' }}>{r.nome}</strong>
              <span
                style={{
                  padding: '2px 7px',
                  borderRadius: 6,
                  fontSize: '.68rem',
                  fontWeight: 700,
                  background: conf.bg,
                  color: conf.color,
                  border: `1px solid ${conf.border}`,
                }}
              >
                RESPONSÁVEL
              </span>
              {userId ? (
                <span style={{ fontSize: '.68rem', fontWeight: 600, color: '#16a34a', display: 'flex', alignItems: 'center', gap: 3 }}>
                  <CheckCircle2 size={11} /> Conta Criada
                </span>
              ) : (
                <span style={{ fontSize: '.68rem', fontWeight: 600, color: '#64748b', display: 'flex', alignItems: 'center', gap: 3 }}>
                  Sem Conta de Usuário
                </span>
              )}
            </div>

            <div style={{ fontSize: '.76rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: 10, marginTop: 2, flexWrap: 'wrap' }}>
              {email && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Mail size={12} /> {email}
                </span>
              )}
              {r.telefone && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Phone size={11} /> {r.telefone}
                </span>
              )}
              {r.alunos && r.alunos.length > 0 && (
                <span style={{ color: '#7e22ce', fontWeight: 600 }}>
                  Filho(a): {r.alunos.map((a) => a.aluno?.nome).filter(Boolean).join(', ')}
                </span>
              )}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {email && <CopyButton text={email} label="E-mail" buttonText="Copiar E-mail" />}
          {directLink && <CopyButton text={directLink} label="Link Direto" buttonText="Copiar Link" />}

          {userId && (
            <button
              onClick={() => handleGenerateNewLink(userId, r.nome)}
              disabled={isGenerating}
              type="button"
              style={{
                background: '#f8fafc',
                border: '1.5px solid #cbd5e1',
                color: '#475569',
                borderRadius: 6,
                padding: '5px 9px',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                fontSize: '.74rem',
                fontWeight: 600,
              }}
            >
              <RefreshCw size={11} className={isGenerating ? 'animate-spin' : ''} />
              {isGenerating ? 'Gerando...' : 'Gerar Novo Link'}
            </button>
          )}

          {userId && (
            <button
              onClick={() => impersonate(userId)}
              disabled={isThisImpersonating}
              type="button"
              style={{
                background: 'linear-gradient(135deg, #7e22ce 0%, #6b21a8 100%)',
                border: 'none',
                color: '#ffffff',
                borderRadius: 6,
                padding: '5px 12px',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                fontSize: '.76rem',
                fontWeight: 700,
                boxShadow: '0 2px 6px rgba(126, 34, 206, 0.25)',
              }}
            >
              <LogIn size={12} />
              {isThisImpersonating ? 'Abrindo...' : 'Entrar'}
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        background: 'rgba(15, 23, 42, 0.65)',
        backdropFilter: 'blur(5px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: '#ffffff',
          borderRadius: 16,
          width: '100%',
          maxWidth: 960,
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
          border: '1.5px solid #e2e8f0',
          overflow: 'hidden',
        }}
      >
        {/* ── CABEÇALHO ── */}
        <div
          style={{
            padding: '16px 22px',
            background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)',
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: 'rgba(99, 102, 241, 0.2)',
                border: '1.5px solid rgba(99, 102, 241, 0.4)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#a5b4fc',
              }}
            >
              <KeyRound size={20} />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800, color: '#ffffff' }}>
                Central de Acessos Rápidos · {escolaNome}
              </h2>
              <p style={{ margin: 0, fontSize: '.76rem', color: '#cbd5e1', display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                <span>{dados?.escola?.municipio || 'Município'} - {dados?.escola?.sigla || 'UF'}</span>
                <span>·</span>
                <span>{dados?.escola?.rede || 'Rede Geral'}</span>
                <span>·</span>
                <span>{totalGeral} acessos mapeados</span>
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.1)',
              border: 'none',
              color: '#ffffff',
              borderRadius: 8,
              width: 32,
              height: 32,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* ── BARRA DE BUSCA E FILTROS EM CHIPS ── */}
        <div
          style={{
            padding: '12px 20px',
            background: '#f8fafc',
            borderBottom: '1.5px solid #e2e8f0',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <div style={{ position: 'relative' }}>
            <Search
              size={15}
              style={{
                position: 'absolute',
                left: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#64748b',
              }}
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Pesquisar por nome, e-mail, cargo ou filho..."
              style={{
                width: '100%',
                padding: '8px 12px 8px 36px',
                borderRadius: 8,
                border: '1.5px solid #cbd5e1',
                background: '#ffffff',
                color: '#0f172a',
                fontSize: '.84rem',
                outline: 'none',
                boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {[
              { id: 'TODOS', label: `Todos (${totalGeral})` },
              { id: 'MUNICIPAL', label: `Gestão Municipal (${totalGestores})` },
              { id: 'DIRECAO', label: `Direção (${totalDiretores})` },
              { id: 'EQUIPE', label: `Equipe Gestora (${totalEquipe})` },
              { id: 'PROFESSOR', label: `Professores (${totalProfessores})` },
              { id: 'RESPONSAVEL', label: `Responsáveis (${totalResponsaveis})` },
            ].map((tab) => {
              const active = activeCategory === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveCategory(tab.id)}
                  style={{
                    padding: '5px 12px',
                    borderRadius: 20,
                    border: active ? '1.5px solid #2563eb' : '1.5px solid #e2e8f0',
                    background: active ? '#2563eb' : '#ffffff',
                    color: active ? '#ffffff' : '#475569',
                    fontSize: '.76rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    transition: 'all .15s ease',
                  }}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── CORPO DA LISTA (SCROLLABLE) ── */}
        <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <Loader2 className="animate-spin" size={18} /> Carregando lista de acessos reais...
            </div>
          ) : (
            <>
              {/* SEÇÃO 1: GESTORES MUNICIPAIS */}
              {(activeCategory === 'TODOS' || activeCategory === 'MUNICIPAL') && gestoresFiltrados.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ fontSize: '.76rem', fontWeight: 800, color: '#1d4ed8', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Building2 size={13} /> Gestão Municipal · Secretaria de Educação ({gestoresFiltrados.length})
                  </div>
                  {gestoresFiltrados.map((u) => renderUserCard(u, 'MUNICIPAL'))}
                </div>
              )}

              {/* SEÇÃO 2: DIREÇÃO */}
              {(activeCategory === 'TODOS' || activeCategory === 'DIRECAO') && diretoresFiltrados.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ fontSize: '.76rem', fontWeight: 800, color: '#047857', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Shield size={13} /> Direção Escolar ({diretoresFiltrados.length})
                  </div>
                  {diretoresFiltrados.map((u) => renderUserCard(u, 'DIRETOR'))}
                </div>
              )}

              {/* SEÇÃO 3: EQUIPE ESCOLAR */}
              {(activeCategory === 'TODOS' || activeCategory === 'EQUIPE') && equipeFiltrada.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ fontSize: '.76rem', fontWeight: 800, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Users size={13} /> Equipe Gestora & Apoio ({equipeFiltrada.length})
                  </div>
                  {equipeFiltrada.map((u) => renderUserCard(u, 'EQUIPE'))}
                </div>
              )}

              {/* SEÇÃO 4: PROFESSORES */}
              {(activeCategory === 'TODOS' || activeCategory === 'PROFESSOR') && professoresFiltrados.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ fontSize: '.76rem', fontWeight: 800, color: '#b45309', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <GraduationCap size={13} /> Corpo Docente · Professores ({professoresFiltrados.length})
                  </div>
                  {professoresFiltrados.map((u) => renderUserCard(u, 'PROFESSOR'))}
                </div>
              )}

              {/* SEÇÃO 5: RESPONSÁVEIS */}
              {(activeCategory === 'TODOS' || activeCategory === 'RESPONSAVEL') && responsaveisFiltrados.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ fontSize: '.76rem', fontWeight: 800, color: '#7e22ce', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <HeartHandshake size={13} /> Pais e Responsáveis ({responsaveisFiltrados.length})
                  </div>
                  {responsaveisFiltrados.map((r) => renderResponsavelCard(r))}
                </div>
              )}

              {gestoresFiltrados.length === 0 &&
                diretoresFiltrados.length === 0 &&
                equipeFiltrada.length === 0 &&
                professoresFiltrados.length === 0 &&
                responsaveisFiltrados.length === 0 && (
                  <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
                    Nenhum usuário encontrado para os filtros selecionados.
                  </div>
                )}
            </>
          )}
        </div>

        {/* ── RODAPÉ ── */}
        <div
          style={{
            padding: '12px 20px',
            background: '#f8fafc',
            borderTop: '1.5px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span style={{ fontSize: '.75rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Sparkles size={13} style={{ color: '#6366f1' }} />
            Clique em <strong>Entrar</strong> para abrir uma sessão imediata em nova aba sem sobrescrever seu login.
          </span>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '6px 16px',
              borderRadius: 8,
              border: '1.5px solid #cbd5e1',
              background: '#ffffff',
              color: '#334155',
              fontWeight: 700,
              fontSize: '.82rem',
              cursor: 'pointer',
            }}
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
